import React from 'react'
import ProfessionalChartLoader from '../../../common/components/molecule/Chart/ChartLoaderView/ProfessionalChartLoader'
import ChartErrorView from '../../../common/components/molecule/Chart/ChartLoaderView/ChartErrorView'
import { EChartsPresenter } from '../../../common/components/molecule/EChartsView'
import { EChartsData } from '../../../common/components/molecule/EChartsView/EChartsView'
import { HistogramBin } from '../types/joulyDailyTypes'

type Props = {
  data: HistogramBin[]
  loading?: boolean
  error?: Error | null
}

const TITLE = 'Distribution de consommation journalière'

/** Map bin key to human-readable axis label */
function binLabel(bin: string): string {
  if (bin === 'Zeros') return '0 kWh'
  // "<n>_bin" where n is the 0-based bucket index (each bucket = 0.4 kWh wide)
  const match = bin.match(/^(\d+)_bin$/)
  if (match) {
    const n = parseInt(match[1], 10)
    const lo = (n * 0.4).toFixed(1)
    const hi = ((n + 1) * 0.4).toFixed(1)
    return `${lo}–${hi}`
  }
  return bin
}

/** Sort bins: "Zeros" first, then numeric bins ascending by parsed index */
function sortBins(bins: HistogramBin[]): HistogramBin[] {
  return [...bins].sort((a, b) => {
    if (a.bin === 'Zeros') return -1
    if (b.bin === 'Zeros') return 1
    const matchA = a.bin.match(/^(\d+)_bin$/)
    const matchB = b.bin.match(/^(\d+)_bin$/)
    const nA = matchA ? parseInt(matchA[1], 10) : Infinity
    const nB = matchB ? parseInt(matchB[1], 10) : Infinity
    return nA - nB
  })
}

const ZEROS_COLOR = '#9ca3af'
const THEME_COLOR = '#10b981'

export default function HistogramPresenter({ data, loading, error }: Props) {
  if (loading) {
    return <ProfessionalChartLoader metricName={TITLE} />
  }

  if (error) {
    return <ChartErrorView metricName={TITLE} error={error} />
  }

  if (!data || data.length === 0) {
    return (
      <EChartsPresenter
        data={[]}
        title={TITLE}
        type="bar"
        height={400}
      />
    )
  }

  const sorted = sortBins(data)

  const chartData: EChartsData[] = sorted.map((bin) => ({
    name: binLabel(bin.bin),
    value: bin.count,
    color: bin.bin === 'Zeros' ? ZEROS_COLOR : THEME_COLOR,
  }))

  return (
    <EChartsPresenter
      data={chartData}
      title={TITLE}
      type="bar"
      height={400}
      xAxisLabel="Consommation moyenne (kWh/jour)"
      yAxisLabel="Nombre d'appareils"
      showLegend={false}
      showTooltip={true}
    />
  )
}
